import React, { useEffect, useState } from 'react';
import { DollarSign, CreditCard, TrendingUp, AlertTriangle, Clock, CheckCircle, ArrowRight, Building2, Search, ReceiptText, Landmark, Wallet, Copy } from 'lucide-react';
import { getSupplierDebt, getPembelian, getPembayaranHutang, getReimbursementDebt } from '../../api';
import { useNavigate } from 'react-router-dom';

export default function RingkasanHutang() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [debts, setDebts] = useState([]);
  const [pembelianList, setPembelianList] = useState([]);
  const [pembayaranList, setPembayaranList] = useState([]);
  const [reimbList, setReimbList] = useState([]);
  const [search, setSearch] = useState('');
  const [copiedRek, setCopiedRek] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([getSupplierDebt(), getPembelian(), getPembayaranHutang(), getReimbursementDebt()])
      .then(([debtRes, beliRes, bayarRes, reimbRes]) => {
        setDebts(debtRes.data || []);
        setPembelianList(beliRes.data || []);
        setPembayaranList(bayarRes.data || []);
        setReimbList(reimbRes.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rupiah = (v) => `Rp ${(Number(v) || 0).toLocaleString('id-ID')}`;

  // Salin nomor rekening supplier supaya bisa langsung ditempel di m-banking
  const handleCopyRek = async (d) => {
    try {
      await navigator.clipboard.writeText(d.no_rekening || '');
      setCopiedRek(d.supplier_name || '');
      setTimeout(() => setCopiedRek(''), 1600);
    } catch (err) {
      // clipboard tidak tersedia (mis. http) — nomor tetap terlihat
    }
  };

  // Calculate totals
  const totalHutang = pembelianList.reduce((s, p) => s + (p.total_amount || 0), 0);
  const totalDibayar = pembayaranList.reduce((s, p) => s + (p.jumlah || 0), 0);
  const totalSisa = debts.reduce((s, d) => s + (d.sisa_hutang || 0), 0);

  // Determine debt status based on TOP
  const getDebtStatus = (supplierName) => {
    const supplierBeli = pembelianList.filter(p =>
      p.supplier_name?.toLowerCase() === supplierName.toLowerCase() && p.top > 0
    );
    if (supplierBeli.length === 0) return 'normal';

    const today = new Date();
    let hasOverdue = false;
    let hasNearDue = false;
    let topTerbesar = 0;

    for (const p of supplierBeli) {
      if (p.top > topTerbesar) topTerbesar = p.top;
      const tglBeli = new Date(p.tanggal + 'T00:00:00');
      const dueDate = new Date(tglBeli);
      dueDate.setDate(dueDate.getDate() + p.top);
      const diffDays = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));

      if (diffDays > 0) hasOverdue = true;
      else if (diffDays >= -7) hasNearDue = true;
    }

    if (hasOverdue) return 'overdue';
    if (hasNearDue || topTerbesar >= 30) return 'warning';
    return 'normal';
  };

  const statusConfig = {
    overdue: { color: '#dc2626', bg: '#fef2f2', label: 'Jatuh Tempo', icon: AlertTriangle },
    warning: { color: '#d97706', bg: '#fffbeb', label: 'Perlu Perhatian', icon: Clock },
    normal: { color: '#059669', bg: '#ecfdf5', label: 'Normal', icon: CheckCircle },
  };

  const filteredDebts = debts.filter(d =>
    !search || d.supplier_name?.toLowerCase().includes(search.toLowerCase())
  );

  // Utang reimbursement karyawan (ditalangi petugas/teknisi) — dipisah dari hutang supplier
  const totalReimbSisa = reimbList.reduce((s, r) => s + (Number(r.sisa) || 0), 0);
  const totalReimbDibayar = reimbList.reduce((s, r) => s + (Number(r.dibayar) || 0), 0);
  const orangReimbAktif = reimbList.filter(r => Number(r.sisa) > 0);
  const reimbAktif = orangReimbAktif
    .filter(r => !search || String(r.nama_petugas || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (Number(b.sisa) || 0) - (Number(a.sisa) || 0));

  if (loading) return <div className="page-loading">Memuat ringkasan hutang...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2><DollarSign size={20} style={{ marginRight: 8 }} />Ringkasan Hutang Supplier</h2>
            <p className="page-desc">Pantau & kelola hutang pembelian ke supplier</p>
          </div>
          <div>
            <button className="btn btn-primary" onClick={() => navigate('/pembayaran-hutang')}>
              <CreditCard size={16} /><span>Bayar Hutang</span>
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginBottom: 20 }}>
        <div className="stat-card" style={{ borderLeft: '4px solid #4f6ef7' }}>
          <div className="stat-card-header"><TrendingUp size={16} /><span>Total Hutang</span></div>
          <div className="stat-card-value" style={{ color: '#4f6ef7', fontSize: '1.3rem' }}>{rupiah(totalHutang)}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #059669' }}>
          <div className="stat-card-header"><CheckCircle size={16} /><span>Sudah Dibayar</span></div>
          <div className="stat-card-value" style={{ color: '#059669', fontSize: '1.3rem' }}>{rupiah(totalDibayar)}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #d97706' }}>
          <div className="stat-card-header"><DollarSign size={16} /><span>Sisa Hutang</span></div>
          <div className="stat-card-value" style={{ color: '#d97706', fontSize: '1.3rem' }}>{rupiah(totalSisa)}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #dc2626' }}>
          <div className="stat-card-header"><Building2 size={16} /><span>Supplier</span></div>
          <div className="stat-card-value" style={{ color: '#dc2626', fontSize: '1.3rem' }}>{debts.length}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #7c3aed' }}>
          <div className="stat-card-header"><Wallet size={16} /><span>Reimbursement Karyawan</span></div>
          <div className="stat-card-value" style={{ color: '#7c3aed', fontSize: '1.3rem' }}>{rupiah(totalReimbSisa)}</div>
        </div>
      </div>

      {/* Search */}
      <div className="search-box" style={{ marginBottom: 14 }}>
        <Search size={18} />
        <input type="text" placeholder="Cari supplier..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Supplier Debt Table */}
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Supplier</th>
              <th>Rekening Tujuan Transfer</th>
              <th style={{ textAlign: 'right' }}>Sisa Hutang</th>
              <th style={{ textAlign: 'center' }}>Status</th>
              <th style={{ textAlign: 'center' }}>TOP</th>
              <th style={{ width: 230 }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filteredDebts.map((d, i) => {
              const status = getDebtStatus(d.supplier_name);
              const cfg = statusConfig[status];
              const Icon = cfg.icon;
              const supplierBeli = pembelianList.filter(p =>
                p.supplier_name?.toLowerCase() === d.supplier_name.toLowerCase() && p.top > 0
              );
              const topMax = supplierBeli.length > 0 ? Math.max(...supplierBeli.map(p => p.top)) : 0;

              return (
                <tr key={i}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{d.supplier_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                      {supplierBeli.length} transaksi
                    </div>
                  </td>
                  <td>
                    {d.no_rekening ? (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                        <div>
                          {d.nama_bank ? (
                            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#4f6ef7', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                              {d.nama_bank}
                            </div>
                          ) : null}
                          <div style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600 }}>{d.no_rekening}</div>
                          {d.nama_rekening ? (
                            <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>a.n. {d.nama_rekening}</div>
                          ) : null}
                        </div>
                        <button
                          className="btn-icon"
                          title={copiedRek === d.supplier_name ? 'Tersalin!' : 'Salin nomor rekening'}
                          onClick={() => handleCopyRek(d)}
                          style={{ color: copiedRek === d.supplier_name ? '#16a34a' : undefined }}
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: '0.75rem', color: '#d97706' }} title="Lengkapi di Inventori → Master Supplier">
                        belum diisi
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '0.95rem' }}>
                    {rupiah(d.sisa_hutang)}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 10px', borderRadius: 12, fontSize: '0.72rem',
                      fontWeight: 600, background: cfg.bg, color: cfg.color
                    }}>
                      <Icon size={12} />{cfg.label}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', fontSize: '0.85rem' }}>
                    {topMax > 0 ? `${topMax} hari` : 'Kontan'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-sm"
                        style={{
                          background: '#4f6ef7', color: 'white', border: 'none',
                          padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                          fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4
                        }}
                        title={`Bayar hutang supplier ini (nota ${d.supplier_name} saja)`}
                        onClick={() => navigate('/pembayaran-hutang', { state: { supplier: d.supplier_name, mode: 'nota' } })}
                      >
                        <CreditCard size={12} /> Bayar
                      </button>
                      <button
                        className="btn btn-sm"
                        style={{
                          background: '#059669', color: 'white', border: 'none',
                          padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                          fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4
                        }}
                        title="Cari & bayar berdasarkan No. Pembelian (semua supplier)"
                        onClick={() => navigate('/pembayaran-hutang', { state: { mode: 'nota', allSuppliers: true } })}
                      >
                        <ReceiptText size={12} /> Bayar per Nota
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        style={{ padding: '5px 10px', fontSize: '0.75rem' }}
                        onClick={() => navigate(`/inventori/pembelian?supplier=${encodeURIComponent(d.supplier_name)}`)}
                      >
                        <ArrowRight size={12} /> Detail
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredDebts.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted" style={{ padding: 40 }}>
                {search ? 'Tidak ada supplier ditemukan' : 'Belum ada hutang'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Utang Reimbursement Karyawan — dipisah dari hutang supplier */}
      {reimbList.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8, color: '#6d28d9' }}>
                <Landmark size={18} /> Utang Reimbursement Karyawan
              </h3>
              <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginTop: 2 }}>
                Pembelian yang ditalangi dulu oleh petugas/teknisi dengan uang pribadi — pelunasannya ke karyawan,
                bukan ke supplier (akun 2211)
              </div>
            </div>
            <button className="btn btn-secondary" onClick={() => navigate('/rekap-reimbursement')}>
              <ReceiptText size={14} /><span>Rekap Lengkap &amp; Pelunasan</span>
            </button>
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Petugas / Teknisi</th>
                  <th style={{ textAlign: 'center' }}>Nota</th>
                  <th style={{ textAlign: 'right' }}>Total Ditalangi</th>
                  <th style={{ textAlign: 'right' }}>Sudah Dibayar</th>
                  <th style={{ textAlign: 'right' }}>Sisa</th>
                  <th style={{ width: 150 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {reimbAktif.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{r.nama_petugas}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{r.jumlah_nota} nota ditalangi</div>
                    </td>
                    <td style={{ textAlign: 'center' }}>{r.jumlah_nota}</td>
                    <td style={{ textAlign: 'right' }}>{rupiah(r.total)}</td>
                    <td style={{ textAlign: 'right', color: '#059669' }}>{rupiah(r.dibayar)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{rupiah(r.sisa)}</td>
                    <td>
                      <button
                        className="btn btn-sm"
                        style={{
                          background: '#7c3aed', color: 'white', border: 'none',
                          padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                          fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4,
                        }}
                        title="Buka halaman rekap untuk pelunasan dari rekening bank"
                        onClick={() => navigate('/rekap-reimbursement')}
                      >
                        <Wallet size={12} /> Pelunasan
                      </button>
                    </td>
                  </tr>
                ))}
                {reimbAktif.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted" style={{ padding: 24 }}>
                      {search ? 'Tidak ada karyawan yang cocok' : 'Semua reimbursement karyawan sudah lunas'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6d28d9', marginTop: 8 }}>
            Sisa reimbursement karyawan: <strong>{rupiah(totalReimbSisa)}</strong>
            {totalReimbDibayar > 0 && <> · sudah dibayar {rupiah(totalReimbDibayar)}</>}
          </div>
        </div>
      )}

      {/* Summary footer */}
      <div style={{
        marginTop: 16, padding: '12px 18px', borderRadius: 10,
        background: 'linear-gradient(135deg, #f0f9ff, #e6f7ff)',
        border: '1px solid #bae6fd',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div style={{ fontSize: '0.82rem', color: '#475569' }}>
          <span style={{ fontWeight: 600 }}>{filteredDebts.length}</span> supplier dengan total sisa hutang
          <span style={{ marginLeft: 4, color: 'var(--gray-400)' }}>(di luar reimbursement karyawan)</span>
        </div>
        <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0369a1' }}>
          {rupiah(filteredDebts.reduce((s, d) => s + (d.sisa_hutang || 0), 0))}
        </div>
      </div>
    </div>
  );
}
